import { useMemo, useCallback } from 'react';
import { DateTimePicker } from '@/components/DateTimePicker';

interface NPSDDateRangePickerProps {
  startDate: Date | null;
  endDate: Date | null;
  onStartDateChange: (value: Date | null) => void;
  onEndDateChange: (value: Date | null) => void;
}

export function NPSDDateRangePicker({
  startDate,
  endDate,
  onStartDateChange,
  onEndDateChange,
}: NPSDDateRangePickerProps) {
  const activeDatePreset = useMemo((): string => {
    if (!startDate && !endDate) {
      return 'all';
    }

    if (startDate && endDate) {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      const start = new Date(startDate);
      start.setHours(0, 0, 0, 0);
      const end = new Date(endDate);
      end.setHours(0, 0, 0, 0);

      const isSameDay = (d1: Date, d2: Date) =>
        d1.getFullYear() === d2.getFullYear() &&
        d1.getMonth() === d2.getMonth() &&
        d1.getDate() === d2.getDate();

      // Check for "Today"
      if (isSameDay(start, today) && isSameDay(end, today)) {
        return 'today';
      }

      // Check for "Last 7 Days"
      const sevenDaysAgo = new Date(today);
      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 6);
      if (isSameDay(start, sevenDaysAgo) && isSameDay(end, today)) {
        return 'last7';
      }

      // Check for "Last 30 Days"
      const thirtyDaysAgo = new Date(today);
      thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 29);
      if (isSameDay(start, thirtyDaysAgo) && isSameDay(end, today)) {
        return 'last30';
      }
    }

    return 'custom';
  }, [startDate, endDate]);

  const setDatePreset = useCallback((preset: string) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    switch (preset) {
      case 'today':
        onStartDateChange(new Date(today));
        onEndDateChange(new Date(today));
        break;
      case 'last7': {
        const start7 = new Date(today);
        start7.setDate(start7.getDate() - 6);
        onStartDateChange(start7);
        onEndDateChange(new Date(today));
        break;
      }
      case 'last30': {
        const start30 = new Date(today);
        start30.setDate(start30.getDate() - 29);
        onStartDateChange(start30);
        onEndDateChange(new Date(today));
        break;
      }
      case 'all':
        onStartDateChange(null);
        onEndDateChange(null);
        break;
    }
  }, [onStartDateChange, onEndDateChange]);

  const handleStartDateChange = useCallback((value: Date | null) => {
    onStartDateChange(value);
  }, [onStartDateChange]);

  const handleEndDateChange = useCallback((value: Date | null) => {
    onEndDateChange(value);
  }, [onEndDateChange]);

  return (
    <div className="date-range-section">
      <div className="date-range-header">
        <span className="date-range-label">Date Range</span>
        <div className="date-presets">
          <button
            type="button"
            className={`date-preset-btn${activeDatePreset === 'today' ? ' active' : ''}`}
            onClick={() => setDatePreset('today')}
          >Today</button>
          <button
            type="button"
            className={`date-preset-btn${activeDatePreset === 'last7' ? ' active' : ''}`}
            onClick={() => setDatePreset('last7')}
          >Last 7 Days</button>
          <button
            type="button"
            className={`date-preset-btn${activeDatePreset === 'last30' ? ' active' : ''}`}
            onClick={() => setDatePreset('last30')}
          >Last 30 Days</button>
          <button
            type="button"
            className={`date-preset-btn${activeDatePreset === 'all' ? ' active' : ''}`}
            onClick={() => setDatePreset('all')}
          >All Time</button>
        </div>
      </div>
      <div className="field-row">
        <div className="field">
          <label>Start Date</label>
          <DateTimePicker
            value={startDate}
            onChange={handleStartDateChange}
            type="date"
            format="MM/DD/YYYY"
            appendToBody={true}
            clearable={true}
          />
        </div>
        <div className="field">
          <label>End Date</label>
          <DateTimePicker
            value={endDate}
            onChange={handleEndDateChange}
            type="date"
            format="MM/DD/YYYY"
            appendToBody={true}
            clearable={true}
          />
        </div>
      </div>
    </div>
  );
}

(window as any).NPSDDateRangePickerReact = NPSDDateRangePicker;
